import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { pureEscapesLogo, arrowNext as iconArrowNext, arrowPrev as iconArrowPrev } from 'ui/Icons';
import { SvgIcon } from 'ui/SvgIcon';
import PlayCircleOutlineIcon from 'ui/Icons/play-circle-outline.component.svg';
import Checkbox from 'ui/Checkbox';
import { ImagesCarouselStyled } from './ImagesCarouselStyled';
import classNames from 'classnames';

interface iImagesCarouselProps {
  images: {
    displayName: string;
    url: string;
    vimeoVideoId?: string;
    uuid?: string;
  }[];
  showThumbnails: boolean;
  showArrows?: boolean;
  showStatus?: boolean;
  wrapperClassName?: string;
  carouselImageClassName?: string;
  thumbnailsCarouselClassName?: string;
  thumbnailsCarouselWrapperClassName?: string;
  initialSlide?: number;
  /** Enable bulk download mode with checkboxes */
  enableBulkDownload?: boolean;
  /** Currently selected image indices (controlled from parent) */
  selectedIndices?: Set<number>;
  /** Callback when selection changes */
  onSelectionChange?: (indices: Set<number>) => void;
  /** Whether a download is currently in progress */
  isDownloading?: boolean;
}

export const ImagesCarousel: React.FC<iImagesCarouselProps> = ({
  images,
  showArrows = true,
  showStatus = true,
  showThumbnails = false,
  wrapperClassName = '',
  carouselImageClassName = '',
  thumbnailsCarouselClassName = '',
  thumbnailsCarouselWrapperClassName = '',
  initialSlide = 0,
  enableBulkDownload = false,
  selectedIndices,
  onSelectionChange,
  isDownloading = false,
}) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, duration: 30, startIndex: initialSlide });

  // Sync embla → state
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentSlide(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // Sync state → embla (driven by thumbnail clicks)
  const prevSlide = useRef(initialSlide);
  useEffect(() => {
    if (!emblaApi || currentSlide === prevSlide.current) return;
    prevSlide.current = currentSlide;
    emblaApi.scrollTo(currentSlide);
  }, [currentSlide, emblaApi]);

  // Reset thumbnail refs array when images change
  useEffect(() => {
    thumbnailRefs.current = images.map(() => null);
  }, [images]);

  const renderPureEscapesLogo = () => {
    return (
      <div className="w-full flex justify-center flex-col items-center bg-brown-20">
        <img
          className="empty-photo block object-cover"
          src={pureEscapesLogo}
          width="82"
          height="82"
          alt="Pure Escapes logo"
        />
      </div>
    );
  };

  const handleThumbnailClick = (index: number) => {
    prevSlide.current = index;
    setCurrentSlide(index);
    emblaApi?.scrollTo(index);
  };

  // Handler for checkbox selection toggle
  const handleCheckboxToggle = (index: number) => {
    if (!onSelectionChange || !selectedIndices) return;

    const newSelection = new Set(selectedIndices);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    onSelectionChange(newSelection);
  };

  // Check if an image is selectable (has UUID and is not a video)
  const isImageSelectable = (image: { vimeoVideoId?: string; uuid?: string }) => {
    return !image.vimeoVideoId && !!image.uuid;
  };

  // Improved scroll function to ensure the selected thumbnail is fully visible
  // and show a peek of the next thumbnail
  const scrollToThumbnail = useCallback(
    (index: number) => {
      const container = thumbnailsContainerRef.current;
      const thumbnail = thumbnailRefs.current[index];

      if (!container || !thumbnail) return;

      const containerWidth = container.offsetWidth;
      const thumbnailWidth = thumbnail.offsetWidth;
      const thumbnailLeft = thumbnail.offsetLeft;

      // Calculate position that places the thumbnail fully in view
      // with a slight offset to show a peek of the next thumbnail
      const peekAmount = 60; // Amount of next thumbnail to show (in pixels)
      let targetScroll = thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;

      // Adjust to show a peek of the next thumbnail if not the last one
      if (index < images.length - 1) {
        targetScroll = thumbnailLeft - (containerWidth - thumbnailWidth - peekAmount);
      }

      // Ensure we don't scroll beyond the beginning
      targetScroll = Math.max(0, targetScroll);

      // Ensure we don't scroll beyond the end (minus the peek amount if applicable)
      const maxScroll = container.scrollWidth - containerWidth;
      targetScroll = Math.min(targetScroll, maxScroll);

      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth',
      });
    },
    [images.length]
  );

  // Ensure the current thumbnail is in view when the slide changes
  useEffect(() => {
    scrollToThumbnail(currentSlide);
  }, [currentSlide, scrollToThumbnail]);

  const renderThumbnails = () => {
    if (images.length === 0) return null;

    return (
      <div className="relative w-full mt-2">
        <div
          ref={thumbnailsContainerRef}
          className={classNames(
            'flex gap-2 overflow-x-auto scroll-smooth hide-scrollbar',
            thumbnailsCarouselWrapperClassName
          )}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {images.map((image, index) => {
            const isSelected = enableBulkDownload && selectedIndices?.has(index);
            const canSelect = enableBulkDownload && isImageSelectable(image);

            return (
              <div
                key={image.uuid ?? index}
                ref={el => {
                  thumbnailRefs.current[index] = el;
                }}
                className={classNames(
                  'flex-shrink-0 cursor-pointer relative',
                  {
                    'opacity-100': currentSlide === index || isSelected,
                    'ring-2 ring-blue-500': currentSlide === index && !isSelected,
                    'ring-2 ring-teal-100': isSelected,
                    'opacity-50 hover:opacity-100': currentSlide !== index && !isSelected,
                  },
                  thumbnailsCarouselClassName
                )}
                onClick={() => handleThumbnailClick(index)}
              >
                <img src={image.url} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                {image.vimeoVideoId && (
                  <>
                    <div className="absolute inset-0 bg-black/40" />
                    <SvgIcon
                      IconComponent={PlayCircleOutlineIcon}
                      className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] fill-white w-[38px] h-[38px]"
                    />
                  </>
                )}
                {/* Checkbox for bulk download - only show for selectable images */}
                {canSelect && (
                  <div
                    className="absolute top-[5px] left-[5px] z-10"
                    onClick={e => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleCheckboxToggle(index)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (images.length === 0) {
    return renderPureEscapesLogo();
  }

  return (
    <ImagesCarouselStyled $showStatus={showStatus}>
      <div className={classNames(wrapperClassName, { 'opacity-50': isDownloading })}>
        <div className="relative">
          {/* Embla viewport */}
          <div ref={emblaRef} className="overflow-hidden">
            <div className="flex">
              {images.map((image, index) => {
                if (image.vimeoVideoId) {
                  return (
                    <div key={image.uuid ?? index} className={classNames('min-w-0 flex-[0_0_100%] image py-5', carouselImageClassName)}>
                      <iframe
                        src={`https://player.vimeo.com/video/${image.vimeoVideoId}`}
                        width="100%"
                        height="100%"
                        allow="autoplay; fullscreen;"
                      ></iframe>
                    </div>
                  );
                }
                return (
                  <div key={image.uuid ?? index} className={classNames('min-w-0 flex-[0_0_100%] image', carouselImageClassName)}>
                    <img className="featured-photo block w-full h-full object-cover" src={image.url} alt="Hotel photo" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Status badge */}
          {showStatus && (
            <div className="carousel-status">
              {currentSlide + 1}/{images.length}
            </div>
          )}

          {/* Prev arrow */}
          {showArrows && (
            <div className="carousel-arrow-prev select-none" onClick={() => emblaApi?.scrollPrev()}>
              <img src={iconArrowPrev} width="14" height="12" alt="Previous picture" />
            </div>
          )}

          {/* Next arrow */}
          {showArrows && (
            <div className="carousel-arrow-next select-none" onClick={() => emblaApi?.scrollNext()}>
              <img src={iconArrowNext} width="14" height="12" alt="Next picture" />
            </div>
          )}
        </div>

        {showThumbnails && renderThumbnails()}
      </div>
    </ImagesCarouselStyled>
  );
};
